import { each, debounce, isEmpty, isObject } from "lodash";

// =================================
// Basic Logging Middleware ========
// =================================

import { isLocal, isDev, isInternal } from "./env";
let allowSend = false;

// Won't display in console by default
export const debug = function () {
  allowSend && console.debug.apply(null, arguments);
};

export const log = function () {
  allowSend && console.log.apply(null, arguments);
};

export const info = function () {
  allowSend && console.info.apply(null, arguments);
};

export const trace = function () {
  allowSend && console.trace.apply(null, arguments);
};

export const dir = function () {
  allowSend && console.dir.apply(null, arguments);
};

export const table = function () {
  allowSend && console.table.apply(null, arguments);
};

export const warn = function () {
  allowSend && console.warn.apply(null, arguments);
};

export const error = function () {
  allowSend && console.error.apply(null, arguments);
};

export const assert = function () {
  allowSend && console.assert.apply(null, arguments);
};

/**
 * Wrapper for emitting a console group
 * const Sample = new Group("Test", "info", true);
 * Sample.emit("First Message", {error: "Error Message"}).emit("Another message")
 * Sample.close() = Stop adding messages and execute the logs (debounced)
 * Sample.end()   = Immediately execute the messages and close the group;
 * @type  {Group}
 * @param {string}  groupName             "Person"
 * @param {object}  [options]             Object of options if not using default
 * @param {string}  [options.defaultType] "log"
 * @param {boolean} [options.collapsed]   Whether to default the group as collapsed in the window
 * @param {boolean} [options.disable]     Prevent writing to the console
 */
export const Group = class {
  constructor(groupName, { defaultType, collapsed, disable = false } = {}) {
    this.name = groupName;
    this.type = defaultType || "log";
    this.collapsed = collapsed || false;
    this.disabled = disable;
    this.messages = [];
    this.close = debounce(this.end, 2000);
    this.debounce = debounce(this.emit, 500);

    if (disable) {
      allowSend && console.info(`${groupName} log disabled`);
      return;
    }

    if (!console || !console.group) {
      return console.error("Browser does not yet support console.group");
    }

    if (defaultType && !new RegExp(Object.keys(console).join("|")).test(defaultType)) {
      throw new Error(`Invalid console [type] option selected: [${defaultType}]`);
    }

    this.emit(`Created new console group: [${groupName}]`);
  }

  emit(...options) {
    if (this.disabled) {
      return this;
    }
    options.forEach((msg) => this.messages.push(msg));
    this.close();
    return this;
  }

  end() {
    if (this.disabled || !allowSend || isEmpty(this.messages)) return;

    console[this.collapsed ? "groupCollapsed" : "group"](this.name);
    this.messages.forEach((msg) => {
      if (isEmpty(msg)) return;
      isObject(msg) ? each(msg, (_msg, type) => console[type](_msg)) : console[this.type](msg);
    });
    this.messages = [];
    console.groupEnd();

    return this;
  }
};

if (isLocal()) {
  allowSend = true;
  log("logger.js:", "Local env detected - allowing logging");
} else if (isDev()) {
  allowSend = true;
  log("logger.js:", "Dev env detected - allowing logging");
} else if (isInternal()) {
  allowSend = true;
  log("logger.js:", "Internal user detected - allowing logging");
}
